"use client";

import { LAND_BITS, LAND_H, LAND_W } from "../_lib/land-mask";
import { type DotScene, DotSceneCanvas, type Paint } from "./dot-scene";

// square, so the globe sits centred with an even margin
const COLS = 48;
const ROWS = 48;

let land: Uint8Array | null = null;
function isLand(lon: number, lat: number): boolean {
  if (!land) {
    const bin = atob(LAND_BITS);
    land = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      land[i] = bin.charCodeAt(i);
    }
  }
  const col = Math.floor((((lon + 180) % 360) + 360) % 360);
  const row = Math.min(LAND_H - 1, Math.max(0, Math.floor(90 - lat)));
  const i = row * LAND_W + col;
  return ((land[i >> 3] ?? 0) & (1 << (i & 7))) !== 0;
}

/** A turning globe, land as full dots and sea as fine ones, shaded toward the limb. */
const GLOBE: DotScene = {
  cols: COLS,
  rows: ROWS,
  still: 14_000,
  create: () => (p: Paint, t: number) => {
    const cx = COLS / 2;
    const cy = ROWS / 2;
    const R = 22.5;
    const spin = (t / 1000) * 9; // degrees: one turn every 40 s
    const tilt = (20 * Math.PI) / 180;
    const S = 3;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        let sum = 0;
        for (let sy = 0; sy < S; sy++) {
          for (let sx = 0; sx < S; sx++) {
            const x = (col + (sx + 0.5) / S - cx) / R;
            const y = (cy - (row + (sy + 0.5) / S)) / R;
            const d2 = x * x + y * y;
            if (d2 > 1) {
              continue;
            }
            const z = Math.sqrt(1 - d2);
            // tip the north pole toward the viewer
            const Y = y * Math.cos(tilt) + z * Math.sin(tilt);
            const Z = z * Math.cos(tilt) - y * Math.sin(tilt);
            const lat =
              (Math.asin(Math.max(-1, Math.min(1, Y))) * 180) / Math.PI;
            const lon = (Math.atan2(x, Z) * 180) / Math.PI + spin;
            const base = isLand(lon, lat) ? 0.9 : 0.16;
            sum += base * (0.45 + 0.55 * z);
          }
        }
        p.tones[row * COLS + col] = sum / (S * S);
      }
    }
  },
};

export function Globe({ className }: { className?: string }) {
  return (
    <DotSceneCanvas
      scene={GLOBE}
      label="A globe turning slowly, drawn in dots"
      className={className}
    />
  );
}
